import { DateTime, Drawer, Icon, RouteLink } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';

const HistoryItem = styled.div`
  position: relative;
  margin-left: 1.25rem;
  padding-left: 3.75rem;
  padding-bottom: 2rem;
  border-left: solid 2px ${colors.grey10};

  &:last-child {
    padding-bottom: 0;
    border-color: transparent;
  }
`;

const HistoryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  position: absolute;
  top: 0;
  left: -1.3125rem;
  color: ${({ color }) => color};
  font-size: ${({ size }) => size};
  background-color: ${colors.white};
  border: solid 3px ${({ color }) => color};
  border-radius: 50%;
`;

const HistoryTitle = styled.div`
  font-weight: ${weights.bold};
`;

const HistoryDetails = styled.div`
  font-size: 0.875rem;
`;

const HistoryNotes = styled.div`
  margin-top: 0.75rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  font-style: italic;
  white-space: pre-wrap;
`;

function InvoiceHistoryIcon({ color = colors.black, size = '1rem', ...props }) {
  return (
    <HistoryIcon color={color} size={size}>
      <Icon {...props} />
    </HistoryIcon>
  );
}

export default function InvoiceHistoryDrawer({ invoiceId, onClose }) {
  const api = useApi();
  const [query, setQuery] = useState({ status: 'loading', data: null });
  const { workspace } = useWorkspace();

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).invoices(invoiceId).history();

    setQuery({ status: 'ready', data });
  }, [api, workspace.id, invoiceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Drawer isOpen title="Invoice History" onClose={onClose}>
      {query.data?.map((item) => (
        <HistoryItem key={item.id}>
          {
            {
              created: <InvoiceHistoryIcon icon="plus" color={colors.primary} size="1rem" />,
              imported: <InvoiceHistoryIcon icon="file-import" color={colors.primary} />,
              updated: <InvoiceHistoryIcon icon="sync" color={colors.primary} size="0.875rem" />,
              published: <InvoiceHistoryIcon icon="inbox-in" color={colors.primary} />,
              unpublished: <InvoiceHistoryIcon icon="times" color={colors.primary} />,
              sent: <InvoiceHistoryIcon icon="inbox-out" color={colors.primary} />,
              viewed: <InvoiceHistoryIcon icon="eye" color={colors.primary} />,
              payment_received: <InvoiceHistoryIcon icon="dollar-sign" color={colors.primary} />,
              payment_updated: <InvoiceHistoryIcon icon="sync" color={colors.primary} />,
              payment_removed: <InvoiceHistoryIcon icon="times" color={colors.primary} />,
              credit_note_applied: <InvoiceHistoryIcon icon="dollar-sign" color={colors.primary} />,
              credit_note_updated: <InvoiceHistoryIcon icon="sync" color={colors.primary} />,
              credit_note_removed: <InvoiceHistoryIcon icon="times" color={colors.primary} />,
              saved_to_qbo: <InvoiceHistoryIcon icon="cloud-upload-alt" color={colors.primary} />,
              saved_to_xero: <InvoiceHistoryIcon icon="cloud-upload-alt" color={colors.primary} />,
              saved_to_sage: <InvoiceHistoryIcon icon="cloud-upload-alt" color={colors.primary} />,
            }[item.status.id]
          }
          <HistoryTitle>
            {item.clientPaymentId ? (
              <RouteLink to={`/app/${workspace.key}/billing/payments/${item.clientPaymentId}/view`}>
                {item.status.name}
              </RouteLink>
            ) : item.creditNoteId ? (
              <RouteLink to={`/app/${workspace.key}/billing/credit-notes/${item.creditNoteId}`}>
                {item.status.name}
              </RouteLink>
            ) : (
              item.status.name
            )}
          </HistoryTitle>

          <HistoryDetails>
            <DateTime value={item.eventTime} format="l [at] LT" />{' '}
            {item.status.id !== 'viewed' && (
              <>
                by {item.member?.name ?? 'Ruddr'}
                {item.source.id !== 'system' && ` via ${item.source.name}`}
              </>
            )}
          </HistoryDetails>

          {item.notes && <HistoryNotes>{item.notes}</HistoryNotes>}
        </HistoryItem>
      ))}
    </Drawer>
  );
}
